/**
 * Toolbox Section Component
 * Main container for the technician's private tool inventory
 * Displayed in Technician Passport
 */

import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Lock, Wrench, Loader2, FileUp, ChevronDown, ChevronUp, DollarSign } from 'lucide-react';
import { ToolCard } from './ToolCard';
import { ToolCreateDialog } from './ToolCreateDialog';
import { ToolImportDialog } from './ToolImportDialog';
import { ToolCategoryBadge } from './ToolCategoryBadge';
import { useToolboxTools, useIsOwnProfile } from '@/hooks/useToolbox';
import { PassportSectionFallback } from '@/components/passport/PassportSectionFallback';
import {
  type ToolboxCategory,
  type TechnicianToolWithMedia,
  TOOLBOX_CATEGORY_LABELS,
  TOOLBOX_CATEGORY_ORDER,
} from '@/types/toolbox';

interface ToolboxSectionProps {
  technicianProfileId: string;
}

export function ToolboxSection({ technicianProfileId }: ToolboxSectionProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ToolboxCategory | 'all'>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<TechnicianToolWithMedia | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const { isOwnProfile, isLoading: isCheckingOwnership } = useIsOwnProfile(technicianProfileId);
  const { data: tools, isLoading: isLoadingTools, isError: toolsFailed } = useToolboxTools(technicianProfileId);

  // Filter tools based on search and category
  const filteredTools = useMemo(() => {
    if (!tools) return [];

    return tools.filter((tool) => {
      // Category filter
      if (selectedCategory !== 'all' && tool.category !== selectedCategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          tool.name.toLowerCase().includes(query) ||
          tool.brand?.toLowerCase().includes(query) ||
          tool.model?.toLowerCase().includes(query) ||
          tool.serial_number?.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [tools, searchQuery, selectedCategory]);

  // Calculate total toolbox value
  const totalToolValue = useMemo(() => {
    if (!tools) return 0;
    return tools.reduce((sum, tool) => sum + (tool.purchase_price || 0), 0);
  }, [tools]);

  // Get categories that have tools (for filter pills)
  const categoriesWithTools = useMemo(() => {
    if (!tools) return new Set<ToolboxCategory>();
    return new Set(tools.map((t) => t.category));
  }, [tools]);

  const handleEditTool = (tool: TechnicianToolWithMedia) => {
    setEditingTool(tool);
    setIsCreateOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setIsCreateOpen(open);
    if (!open) {
      setEditingTool(null);
    }
  };

  // Loading state
  if (isCheckingOwnership || isLoadingTools) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wrench className="h-5 w-5" />
            Toolbox
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Core-100 R4 W1 (CORE-K, failure is not absence): the owner's tools could not be read. Say so — an empty
  // toolbox here would tell the person they own nothing.
  if (isOwnProfile && toolsFailed) {
    return <PassportSectionFallback sectionName="Toolbox" />;
  }

  // Private section for non-owners
  if (!isOwnProfile) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wrench className="h-5 w-5" />
            Toolbox
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Lock className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              Toolbox is private to the technician
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex flex-col gap-1">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Wrench className="h-5 w-5" />
              Toolbox
              {tools && tools.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {tools.length}
                </Badge>
              )}
            </CardTitle>
            {totalToolValue > 0 && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <DollarSign className="h-3.5 w-3.5" />
                <span>Total Value: <span className="font-semibold text-foreground">${totalToolValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setIsImportOpen(true)}>
              <FileUp className="h-4 w-4 mr-1" />
              Import PDF
            </Button>
            <Button size="sm" onClick={() => setIsCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Add Tool
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Search & Filters */}
          {tools && tools.length > 0 && (
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tools..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Category Filter Pills */}
              {categoriesWithTools.size > 1 && (
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => setSelectedCategory('all')}
                  >
                    All
                  </Badge>
                  {TOOLBOX_CATEGORY_ORDER.filter((cat) => categoriesWithTools.has(cat)).map(
                    (cat) => (
                      <Badge
                        key={cat}
                        variant={selectedCategory === cat ? 'default' : 'outline'}
                        className="cursor-pointer hover:bg-primary/80"
                        onClick={() => setSelectedCategory(cat)}
                      >
                        {TOOLBOX_CATEGORY_LABELS[cat]}
                      </Badge>
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tools Grid */}
          {filteredTools.length > 0 ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(isExpanded ? filteredTools : filteredTools.slice(0, 4)).map((tool) => (
                  <ToolCard
                    key={tool.id}
                    tool={tool}
                    isEditable={true}
                    onEdit={handleEditTool}
                  />
                ))}
              </div>
              {filteredTools.length > 4 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-muted-foreground"
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4 mr-1" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-1" />
                      Show All {filteredTools.length} Tools
                    </>
                  )}
                </Button>
              )}
            </>
          ) : tools && tools.length > 0 ? (
            // Has tools but filtered to zero
            <div className="text-center py-8 text-muted-foreground">
              <p>No tools match your search</p>
            </div>
          ) : (
            // Empty state - no tools yet
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Wrench className="h-12 w-12 mx-auto text-muted-foreground/40 mb-3" />
              <h3 className="font-medium mb-1">Your toolbox is empty</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Start cataloging your professional tools
              </p>
              <Button size="sm" onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Your First Tool
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <ToolCreateDialog
        open={isCreateOpen}
        onOpenChange={handleDialogClose}
        technicianProfileId={technicianProfileId}
        editingTool={editingTool}
      />

      {/* Import Dialog */}
      <ToolImportDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        technicianProfileId={technicianProfileId}
      />
    </>
  );
}
